import React, { useState, memo } from 'react';
import { Button } from '../../../../components/ui/Button';
import {
  DEFAULT_WEBHOOK_HEADERS,
  DEFAULT_WEBHOOK_BODY,
  WEBHOOK_SYSTEM_ATTRS,
} from '../../../../features/webhooks/webhooksConstants';
import KeyValueEditor from './KeyValueEditor';
import { useKeyValueStore } from './useKeyValueStore';

/**
 * Destination, headers and body of one webhook, inside its detail page.
 *
 * The status and substatus are not edited here: they are chosen in the "add"
 * dialog and fixed from then on (see `findWebhookFor`). The page mounts the form
 * again whenever the stored webhook changes, so the pairs are seeded once, from
 * the stored webhook or from the legacy defaults.
 *
 * @param {object} props
 * @param {object} props.initialWebhook - The stored webhook, or `{ status, substatus }` for a new one.
 * @param {boolean} props.isEditing
 * @param {boolean} props.saving
 * @param {(payload: object) => void} props.onSubmit - Receives the legacy payload.
 * @param {(message: string) => void} props.onInvalid
 */
const WebhookForm = ({ initialWebhook, isEditing, saving, onSubmit, onInvalid }) => {
  const [url, setUrl] = useState(isEditing ? initialWebhook.url || "" : "");

  const headersStore = useKeyValueStore(isEditing ? initialWebhook.headers : DEFAULT_WEBHOOK_HEADERS);
  const bodyStore = useKeyValueStore(isEditing ? initialWebhook.body : DEFAULT_WEBHOOK_BODY);

  // `KeyValueEditor` reports with the legacy `(active, message)` signature.
  const handleSnack = (_active, message) => onInvalid(message);

  const handleSaveConfig = (event) => {
    event.preventDefault();

    if (!initialWebhook.status || !initialWebhook.substatus || url === "") {
      onInvalid("Estado/SubEstado y URL son datos requeridos");
      return;
    }

    onSubmit({
      ...initialWebhook,
      url,
      status: initialWebhook.status,
      substatus: initialWebhook.substatus,
      headers: headersStore.getObjectStore(),
      body: bodyStore.getObjectStore(),
    });
  };

  return (
    <form data-testid="webhook-form" onSubmit={handleSaveConfig} noValidate className="space-y-6">
      <div>
        <label htmlFor="webhook-url" className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">
          URL destino
        </label>
        <input
          id="webhook-url"
          value={url}
          placeholder="https://..."
          onChange={(event) => setUrl(event.target.value)}
          className="w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors"
        />
      </div>

      <KeyValueEditor
        title="Cabeceras"
        testId="webhook-headers"
        store={headersStore.store}
        addCustomValue={headersStore.addCustomValue}
        updateKey={headersStore.updateKey}
        updateValue={headersStore.updateValue}
        removeKey={headersStore.removeKey}
        handleSnack={handleSnack}
      />

      <KeyValueEditor
        title="Cuerpo"
        testId="webhook-body"
        store={bodyStore.store}
        addCustomValue={bodyStore.addCustomValue}
        updateKey={bodyStore.updateKey}
        updateValue={bodyStore.updateValue}
        removeKey={bodyStore.removeKey}
        handleSnack={handleSnack}
      />

      <div data-testid="webhook-system-attrs" className="space-y-2">
        <h4 className="text-sm font-bold text-ninja-dark dark:text-white">
          Valores disponibles del sistema
        </h4>
        <div className="flex flex-wrap gap-2">
          {WEBHOOK_SYSTEM_ATTRS.map((attr) => (
            <span
              key={attr}
              className="px-2.5 py-1 rounded-full border border-ninja-green text-ninja-green text-xs font-medium"
            >
              {attr}
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" variant="primary" loading={saving} loadingLabel="Guardando...">
          {isEditing ? "Actualizar webhook" : "Agregar webhook"}
        </Button>
      </div>
    </form>
  );
};

export default memo(WebhookForm);
